import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Clock, ChevronLeft, ChevronRight, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const QuizTake = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quizData, setQuizData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [attemptId, setAttemptId] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);

  // Start quiz
  useEffect(() => {
    startQuiz();
  }, [quizId]);

  // Timer
  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0 || results) return;
    
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, results]);

  const startQuiz = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/assessments/quizzes/${quizId}/start`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Quiz başlatılamadı');
      }

      const data = await response.json();
      setQuizData(data.quiz);
      setQuestions(data.questions);
      setAttemptId(data.attempt.id);
      
      if (data.quiz.duration) {
        setTimeLeft(data.quiz.duration * 60);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleSubmit = useCallback(async () => {
    if (submitting || results) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const formattedAnswers = Object.entries(answers).map(([questionId, answer]) => ({
        questionId,
        ...(typeof answer === 'string' ? { answerText: answer } : { selectedOptionId: answer })
      }));

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/assessments/attempts/${attemptId}/submit`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formattedAnswers)
        }
      );

      if (response.ok) {
        const data = await response.json();
        setResults(data);
      }
    } catch (err) {
      console.error('Gönderim hatası:', err);
      alert('Quiz gönderilemedi. Lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  }, [answers, attemptId, submitting, results]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Quiz yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Quiz Başlatılamadı</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Geri Dön
          </button>
        </div>
      </div>
    );
  }

  // Results Screen
  if (results) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-3xl mx-auto px-4">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Result Header */}
            <div className={`p-8 text-center ${results.passed ? 'bg-green-500' : 'bg-red-500'}`}>
              {results.passed ? (
                <CheckCircle size={64} className="mx-auto text-white mb-4" />
              ) : (
                <XCircle size={64} className="mx-auto text-white mb-4" />
              )}
              <h1 className="text-3xl font-bold text-white mb-2">
                {results.passed ? 'Tebrikler! 🎉' : 'Tekrar Deneyin'}
              </h1>
              <p className="text-white/90">
                {results.passed ? 'Quiz\'i başarıyla tamamladınız' : 'Geçme notuna ulaşamadınız'}
              </p>
            </div>

            {/* Score */}
            <div className="p-8">
              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-3xl font-bold text-blue-600">{results.score}</p>
                  <p className="text-sm text-gray-500">Puan</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-3xl font-bold text-gray-900">{results.totalPoints}</p>
                  <p className="text-sm text-gray-500">Toplam</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className={`text-3xl font-bold ${results.passed ? 'text-green-600' : 'text-red-600'}`}>
                    %{results.percentage}
                  </p>
                  <p className="text-sm text-gray-500">Başarı (Geçme: %{results.passingScore})</p>
                </div>
              </div>

              {/* Question Results */}
              {results.results && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg mb-4">Soru Detayları</h3>
                  {results.results.map((result, index) => (
                    <div 
                      key={result.questionId} 
                      className={`p-4 rounded-lg border-2 ${
                        result.isCorrect ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {result.isCorrect ? (
                          <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={20} />
                        ) : (
                          <XCircle className="text-red-600 flex-shrink-0 mt-1" size={20} />
                        )}
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">
                            Soru {index + 1}: {result.questionText}
                          </p>
                          {result.correctAnswer && (
                            <p className="text-sm text-green-700 mt-1">
                              Doğru cevap: {result.correctAnswer.text}
                            </p>
                          )}
                          {result.explanation && (
                            <p className="text-sm text-gray-600 mt-2 p-2 bg-white rounded">
                              💡 {result.explanation}
                            </p>
                          )}
                        </div>
                        <span className="text-sm font-medium text-gray-500">
                          {result.isCorrect ? result.points : 0}/{result.points} puan
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-8 text-center">
                <button
                  onClick={() => navigate(-1)}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Kursa Dön
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Quiz Taking Screen
  const currentQuestion = questions[currentIndex];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <h1 className="font-bold text-gray-900">{quizData?.title}</h1>
            <p className="text-sm text-gray-500">
              Soru {currentIndex + 1} / {questions.length}
            </p>
          </div>
          
          {timeLeft !== null && (
            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
              timeLeft < 60 ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
            }`}>
              <Clock size={20} />
              <span className="font-mono font-bold">{formatTime(timeLeft)}</span>
            </div>
          )}
        </div>
        
        {/* Progress Bar */}
        <div className="h-1 bg-gray-200">
          <div 
            className="h-full bg-blue-600 transition-all duration-300"
            style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-lg p-6">
          {/* Question Number Indicator */}
          <div className="flex gap-2 mb-6 flex-wrap">
            {questions.map((q, idx) => (
              <button
                key={q.id}
                onClick={() => setCurrentIndex(idx)}
                className={`w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                  idx === currentIndex
                    ? 'bg-blue-600 text-white'
                    : answers[q.id]
                    ? 'bg-green-100 text-green-700 border-2 border-green-300'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {idx + 1}
              </button>
            ))}
          </div>

          {/* Question Text */}
          <div className="mb-6">
            <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm mb-3">
              {currentQuestion.questionType} • {currentQuestion.points} puan
            </span>
            <h2 className="text-xl font-semibold text-gray-900">
              {currentQuestion.questionText}
            </h2>
          </div>

          {/* Options */}
          {(currentQuestion.questionType === 'Çoktan Seçmeli' || currentQuestion.questionType === 'Doğru/Yanlış') && (
            <div className="space-y-3">
              {currentQuestion.options.map((option, idx) => (
                <button
                  key={option.id}
                  onClick={() => handleAnswer(currentQuestion.id, option.id)}
                  className={`w-full p-4 text-left rounded-lg border-2 transition-all ${
                    answers[currentQuestion.id] === option.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      answers[currentQuestion.id] === option.id
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {String.fromCharCode(65 + idx)}
                    </span>
                    <span className="text-gray-900">{option.text}</span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Short Answer */}
          {currentQuestion.questionType === 'Kısa Cevap' && (
            <textarea
              value={answers[currentQuestion.id] || ''}
              onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
              placeholder="Cevabınızı buraya yazın..."
              className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
              rows={4}
            />
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
            disabled={currentIndex === 0}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={20} />
            Önceki
          </button>

          <div className="flex gap-3">
            {currentIndex === questions.length - 1 ? (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
              >
                {submitting ? 'Gönderiliyor...' : '✓ Quiz\'i Bitir'}
              </button>
            ) : (
              <button
                onClick={() => setCurrentIndex(prev => Math.min(questions.length - 1, prev + 1))}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Sonraki
                <ChevronRight size={20} />
              </button>
            )}
          </div>
        </div>

        {/* Answered Count */}
        <div className="text-center mt-4 text-sm text-gray-500">
          {Object.keys(answers).length} / {questions.length} soru cevaplandı
        </div>
      </div>
    </div>
  );
};

export default QuizTake;
